import {
  isNativeBridgeAvailable,
  nativeConnect,
  nativeConnectRfd40Auto,
  nativeDisconnect,
  nativeGetStatus,
  nativeListRfd8500,
  nativeConnectAsync,
  nativeConnectRfd40AutoAsync,
  nativeDisconnectAsync,
  nativeGetStatusAsync,
  nativeListRfd8500Async,
  subscribeNativeConnection,
  subscribeNativeError,
  subscribeNativeTag,
} from './leapNativeBridge'
import type {
  ConnectionEvent,
  ErrorEvent,
  KnownDevice,
  ScannerAdapter,
  ScannerStatus,
  ScannerType,
} from './types'

export class NativeSdkAdapter implements ScannerAdapter {
  readonly kind = 'native' as const

  isAvailable(): boolean {
    return isNativeBridgeAvailable()
  }

  async listDevices(type: ScannerType): Promise<KnownDevice[]> {
    if (type === 'RFD40') {
      return [
        {
          id: 'native:RFD40:auto',
          name: 'RFD40 (auto USB / eConnex)',
          type: 'RFD40',
          source: 'native',
        },
      ]
    }
    const list = (await nativeListRfd8500Async()) ?? nativeListRfd8500()
    return list.map((d) => ({
      id: `native:RFD8500:${(d.address || d.name).toUpperCase()}`,
      name: d.name,
      type: 'RFD8500' as const,
      address: d.address,
      source: 'native' as const,
    }))
  }

  async connect(type: ScannerType, name?: string, address?: string): Promise<void> {
    if (type === 'RFD40') {
      await nativeConnectRfd40AutoAsync().catch(() => nativeConnectRfd40Auto())
      return
    }
    if (!name?.trim() && !address?.trim()) {
      throw new Error('Select an RFD8500 device')
    }
    await nativeConnectAsync(
      'RFD8500',
      name?.trim() || undefined,
      address?.trim() || undefined
    ).catch(() => nativeConnect('RFD8500', name?.trim() || undefined, address?.trim() || undefined))
  }

  async disconnect(): Promise<void> {
    await nativeDisconnectAsync().catch(() => nativeDisconnect())
  }

  async getStatus(): Promise<ScannerStatus | null> {
    return (await nativeGetStatusAsync()) ?? nativeGetStatus()
  }

  subscribeTags(handler: (tagId: string) => void): () => void {
    return subscribeNativeTag((ev) => handler(String(ev.tagID).toUpperCase()))
  }

  subscribeConnection(handler: (ev: ConnectionEvent) => void): () => void {
    return subscribeNativeConnection(handler)
  }

  subscribeError(handler: (ev: ErrorEvent) => void): () => void {
    return subscribeNativeError(handler)
  }
}
